if (!customElements.get('case-study-reel')) {
  customElements.define(
    'case-study-reel',
    class CaseStudyReel extends HTMLElement {
      connectedCallback() {
        this.video = this.querySelector('video');
        this.toggleButton = this.querySelector('[data-video-toggle]');
        this.soundButton = this.querySelector('[data-video-sound]');
        if (!this.video || !this.toggleButton || !this.soundButton) return;

        this.events = new AbortController();
        const options = { signal: this.events.signal };
        this.toggleButton.addEventListener('click', () => this.togglePlayback(), options);
        this.soundButton.addEventListener('click', () => {
          if (this.video.muted || this.video.volume === 0) {
            this.video.muted = false;
            if (this.video.volume === 0) this.video.volume = 1;
          } else {
            this.video.muted = true;
          }
        }, options);
        this.video.addEventListener('play', () => {
          const gallery = this.closest('case-study-gallery');
          gallery?.querySelectorAll('case-study-reel video').forEach((video) => {
            if (video !== this.video) video.pause();
          });
          this.updatePlayback();
        }, options);
        this.video.addEventListener('pause', () => this.updatePlayback(), options);
        this.video.addEventListener('ended', () => this.updatePlayback(), options);
        this.video.addEventListener('volumechange', () => this.updateSound(), options);
        this.video.addEventListener('error', () => this.useNativeControls(), options);
        document.addEventListener('visibilitychange', () => {
          if (document.hidden) this.video.pause();
        }, options);

        // Keep normal browser controls when JavaScript cannot enhance the player.
        if (this.video.error) return;
        this.video.controls = false;
        this.classList.add('is-enhanced');
        this.toggleButton.hidden = false;
        this.soundButton.hidden = false;
        this.updatePlayback();
        this.updateSound();

        if ('IntersectionObserver' in window) {
          this.observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.intersectionRatio < 0.25)) this.video.pause();
          }, { threshold: [0, 0.25] });
          this.observer.observe(this);
        }
      }

      disconnectedCallback() {
        this.events?.abort();
        this.observer?.disconnect();
        this.video?.pause();
        this.useNativeControls();
      }

      async togglePlayback() {
        if (!this.video.paused && !this.video.ended) {
          this.video.pause();
          return;
        }
        try {
          await this.video.play();
        } catch (error) {
          // A swipe or another player can pause a pending play request.
          if (error.name !== 'AbortError') this.useNativeControls();
        }
      }

      updatePlayback() {
        const playing = !this.video.paused && !this.video.ended;
        this.classList.toggle('is-playing', playing);
        this.toggleButton.setAttribute('aria-label', playing ? this.toggleButton.dataset.pauseLabel : this.toggleButton.dataset.playLabel);
      }

      updateSound() {
        const muted = this.video.muted || this.video.volume === 0;
        this.classList.toggle('is-muted', muted);
        this.soundButton.setAttribute('aria-label', muted ? this.soundButton.dataset.unmuteLabel : this.soundButton.dataset.muteLabel);
      }

      useNativeControls() {
        this.classList.remove('is-enhanced');
        if (this.video) this.video.controls = true;
        if (this.toggleButton) this.toggleButton.hidden = true;
        if (this.soundButton) this.soundButton.hidden = true;
      }
    }
  );
}
